import { memo, useRef, useState } from "react";
import { Avatar, InputLabel, Box, IconButton, CircularProgress } from "@mui/material";
import InputEmoji from "react-input-emoji";
import Grid from "@mui/material/Unstable_Grid2";
import dotenv from "react-dotenv";
import Moment from "moment";
import { useTranslation } from "react-i18next";
import ChatMessage from "./ChatMessage";

const chatStyle = {
  chat: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: "0px",
    height: "640px",
    border: "1px solid #EAECF0",
    boxShadow: "10px 15px 60px rgba(0, 0, 0, 0.04)",
    borderRadius: "18px",
    backgroundColor: "white",
  },
  name: {
    fontWeight: "600",
    fontSize: "22px",
    lineHeight: "26px",
    color: "#203482",
  },
  attachedFile: {
    width: "10px",
    height: "10px",
    backgroundColor: "#3988E3",
    borderRadius: "5px",
    position: "absolute",
    right: "0px",
    top: "0px",
  },
};

const MemoChatMessage = memo(ChatMessage);

// Isolated so typing in the input (messageToSend/isAttached state in ChatWindow)
// doesn't re-run .map() over the message list on every keystroke.
const MessageList = memo(function MessageList({ messages, isMessagesLoading, chatUserId, selectedRoom }) {
  if (isMessagesLoading && !messages) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <CircularProgress size={32} />
      </Box>
    );
  }
  return messages?.map((message) => (
    <MemoChatMessage
      key={message.id}
      attachmentFile={
        new URL("/chat/v1/storage/attachments/" + message.attachment?.file, dotenv.API_URL)
      }
      attachmentTitle={message.attachment?.title}
      attachmentType={message.attachment?.type}
      message={message.message}
      fromName={message.from_name}
      fromId={message.from_id}
      userId={chatUserId}
      datetime={message.timeAgo}
      selectedRoom={selectedRoom}
    />
  ));
});

/**
 * Conversation pane: header + message list + input. Pure/presentational —
 * data comes from the caller's useChatRoom() so it can be shared with a
 * contact list (full page) or used standalone (modal).
 */
const ChatWindow = ({
  selectedRoom,
  messages,
  isMessagesLoading,
  chatUserId,
  sendMessage,
  sx,
  maxAttachmentSizeBytes = 10485760,
  showMessage,
}) => {
  const { t } = useTranslation();
  Moment.locale("es");
  const hiddenFileInput = useRef(null);
  const [file, setFile] = useState(null);
  const [isAttached, setIsAttached] = useState(false);
  const [messageToSend, setMessageToSend] = useState("");

  const attachFile = () => {
    hiddenFileInput.current.click();
  };

  const handleAttachChange = (event) => {
    const selected = event.target.files[0];
    if (!selected) return;
    if (selected.size > maxAttachmentSizeBytes) {
      if (showMessage) showMessage(t("attachment_too_big"), () => {}, "error", 4000);
      event.target.value = "";
      return;
    }
    setFile(selected);
    setIsAttached(true);
  };

  const handleSend = () => {
    sendMessage({
      message: messageToSend,
      file,
      onSent: () => {
        setMessageToSend("");
        setFile(null);
        setIsAttached(false);
        if (hiddenFileInput.current) hiddenFileInput.current.value = "";
      },
    });
  };

  if (!selectedRoom) return null;

  return (
    <Box sx={[chatStyle.chat, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]} minWidth={"700px"} maxWidth={"1200px"} ml={"20px"}>
      <Grid xs={12} sx={{ margin: "13px 20px", display: "flex", alignItems: "center" }}>
        <Grid xs={1}>
          <Avatar alt="avatar" src={selectedRoom?.avatar} sx={{ width: "54px", height: "54px" }} />
        </Grid>
        <Grid xs={11} ml={"10px"}>
          <InputLabel sx={chatStyle.name}>{selectedRoom.name}</InputLabel>
        </Grid>
      </Grid>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column-reverse",
          width: "100%",
          marginTop: "20px",
          height: "473px",
          overflowY: "scroll",
        }}
      >
        <MessageList
          messages={messages}
          isMessagesLoading={isMessagesLoading}
          chatUserId={chatUserId}
          selectedRoom={selectedRoom}
        />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }} mt={"5px"}>
        <InputEmoji
          value={messageToSend}
          onChange={setMessageToSend}
          cleanOnEnter
          onEnter={handleSend}
          placeholder={t("write_message")}
        />
        <IconButton onClick={attachFile}>
          <img src={"/chat_assets/attach.png"} />
          <input
            id="file_id"
            type="file"
            ref={hiddenFileInput}
            onChange={handleAttachChange}
            style={{ display: "none" }}
          />
          {isAttached ? <Box sx={chatStyle.attachedFile}></Box> : <></>}
        </IconButton>
        <IconButton onClick={handleSend}>
          <img src={"/chat_assets/send.png"} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ChatWindow;
